import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { getTodayInUserTimezone, isToday } from '../utils/dateUtils';
import { ScheduleJob } from '../hooks/useScheduleData';

interface Customer {
  _id: string;
  id?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  linkedCustomerId?: string | {
    _id?: string;
    id?: string;
  };
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  services?: {
    lawnMowingTrimming?: boolean;
    weedControlFertilizing?: boolean;
    snowRemoval?: boolean;
  };
  servicePricing?: {
    lawnMowingTrimming?: {
      price: number;
      frequency: string;
    } | null;
    weedControlFertilizing?: {
      price: number;
      frequency: string;
    } | null;
    snowRemoval?: {
      price: number;
      frequency: string;
    } | null;
  };
}

interface AddToTodayDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Dialog for adding a customer's service to today's schedule
 * Uses the same approach as CustomerScheduleDialog - fetches actual scheduled jobs
 * and displays them, allowing selection of which service to add to today
 */
const AddToTodayDialog: React.FC<AddToTodayDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerJobs, setCustomerJobs] = useState<ScheduleJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ScheduleJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCustomerInputFocused, setIsCustomerInputFocused] = useState(false);
  const [filterBy, setFilterBy] = useState<'all' | 'customer' | 'lawnMowing' | 'weedControl' | 'snowRemoval' | 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract'>('all');
  const [changeTypeDialog, setChangeTypeDialog] = useState<{
    open: boolean;
    onConfirm: (isPermanent: boolean) => void;
  }>({ open: false, onConfirm: () => {} });
  const todayDate = getTodayInUserTimezone();

  // Fetch customers when dialog opens
  useEffect(() => {
    if (open) {
      fetchCustomers();
    } else {
      // Reset state when dialog closes
      setSelectedCustomer(null);
      setCustomerJobs([]);
      setSelectedJob(null);
      setError(null);
      setIsCustomerInputFocused(false);
    }
  }, [open]);

  // Fetch customer's scheduled jobs when customer is selected
  useEffect(() => {
    if (selectedCustomer && open) {
      fetchCustomerJobs();
    } else {
      setCustomerJobs([]);
      setSelectedJob(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, open]);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await apiService.get('/existing-customers');
      if (response.success && response.data) {
        const customersArray = Array.isArray(response.data) ? response.data : [];
        setCustomers(customersArray);
        console.log('[ADD-TO-TODAY] Loaded customers:', customersArray.length);
      } else {
        console.error('[ADD-TO-TODAY] Failed to fetch customers - no success:', response);
        setError('Failed to load customers');
      }
    } catch (err) {
      console.error('[ADD-TO-TODAY] Failed to fetch customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomerJobs = async () => {
    if (!selectedCustomer) return;

    try {
      setLoadingJobs(true);
      setError(null);

      // Get the property-specific customerId (ExistingCustomer._id) and linked User id
      const propertyCustomerId = (selectedCustomer._id || selectedCustomer.id)?.toString();
      const linkedCustomerId = (selectedCustomer as any).linkedCustomerId;
      const linkedCustomerIdStr = (linkedCustomerId?._id || linkedCustomerId?.id || linkedCustomerId)?.toString();
      const propertyAddress = selectedCustomer.address;

      if (!propertyCustomerId) {
        setError('Customer does not have a valid ID');
        return;
      }

      // Normalize address for comparison
      const normalizeAddress = (addr: any) => {
        if (!addr) return null;
        return {
          street: (addr.street || '').trim().toLowerCase(),
          zipCode: (addr.zipCode || '').trim(),
          city: (addr.city || '').trim().toLowerCase()
        };
      };

      const normalizedPropertyAddress = normalizeAddress(propertyAddress);

      // Job belongs to this property if: existingCustomerId === propertyCustomerId (ExistingCustomer._id),
      // OR customerId === linkedCustomerId (User._id) — ServiceRequests use customerId = User, existingCustomerId = property
      const jobBelongsToProperty = (job: any, reqAddress: ReturnType<typeof normalizeAddress>) => {
        const jobExistingCustomerId = (job.existingCustomerId?._id || job.existingCustomerId?.id || job.existingCustomerId)?.toString();
        const jobCustomerId = (job.customerId?._id || job.customerId?.id || job.customerId)?.toString();
        const byExistingCustomer = jobExistingCustomerId === propertyCustomerId;
        const byLinkedCustomer = linkedCustomerIdStr && jobCustomerId === linkedCustomerIdStr;
        const addressMatches = normalizedPropertyAddress && reqAddress
          ? reqAddress.street === normalizedPropertyAddress.street && reqAddress.zipCode === normalizedPropertyAddress.zipCode
          : true;
        return (byExistingCustomer || byLinkedCustomer) && addressMatches;
      };

      console.log('[ADD-TO-TODAY] Fetching jobs for property:', {
        propertyCustomerId: propertyCustomerId,
        linkedCustomerIdStr,
        customerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        address: propertyAddress ? `${propertyAddress.street}, ${propertyAddress.zipCode}` : 'N/A',
        normalizedAddress: normalizedPropertyAddress
      });

      // Use the same date range calculation as useScheduleData hook
      const todayStr = getTodayInUserTimezone();
      const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
      const todayDayOfWeek = new Date().getDay();
      
      // Calculate start of week (Monday) - same as useScheduleData
      let daysFromMonday;
      if (todayDayOfWeek === 0) {
        daysFromMonday = -1;
      } else {
        daysFromMonday = todayDayOfWeek - 1;
      }
      
      const startDate = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay - daysFromMonday, 0, 0, 0, 0));
      const endDate = new Date(Date.UTC(todayYear, todayMonth + 2, todayDay, 23, 59, 59, 999)); // 3 months from now

      // Fetch scheduled jobs
      const scheduleResponse = await apiService.get(
        `/service-requests/provider/schedule?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
      );

      // Fetch accepted service requests (including those without scheduledDate)
      const acceptedResponse = await apiService.get(
        `/service-requests?userType=provider&status=accepted`
      );

      // Also fetch ExistingCustomer records for this customer to find services that don't have ServiceRequests yet
      // This is important for per-instance services that haven't been scheduled yet
      // A customer can have multiple ExistingCustomer records (one per property), so filter by linkedCustomerId
      // IMPORTANT: We need to get ALL ExistingCustomer records, not just the selected one
      // The selected customer is one ExistingCustomer record, but the customer may have multiple properties
      const existingCustomerResponse = await apiService.get(`/existing-customers`);
      
      console.log('[ADD-TO-TODAY] Selected property details:', {
        propertyCustomerId: propertyCustomerId,
        selectedCustomerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        selectedCustomerAddress: propertyAddress ? `${propertyAddress.street}, ${propertyAddress.city}` : 'No address',
        normalizedAddress: normalizedPropertyAddress
      });

      const allJobs: any[] = [];

      // Add scheduled jobs - match useScheduleData hook format (response.data is array directly)
      // CRITICAL: Filter by property-specific customerId AND address to only show services for this property
      if (scheduleResponse.success && scheduleResponse.data) {
        const allScheduledJobs = Array.isArray(scheduleResponse.data) 
          ? scheduleResponse.data 
          : [];
        
        // Filter to only jobs for this specific property (match by existingCustomerId or customerId === linkedCustomerId)
        const scheduledJobs = allScheduledJobs.filter((j: any) => {
          const jobAddress = normalizeAddress(j.address || j.customerId?.address);
          return jobBelongsToProperty(j, jobAddress);
        });
        
        allJobs.push(...scheduledJobs);
        console.log('[ADD-TO-TODAY] Found scheduled jobs for property:', {
          total: scheduledJobs.length,
          filteredFrom: allScheduledJobs.length,
          propertyCustomerId
        });
      }

      // Add accepted jobs without scheduled date
      // CRITICAL: Filter by property-specific customerId AND address to only show services for this property
      if (acceptedResponse.success && acceptedResponse.data) {
        const acceptedData = acceptedResponse.data as any;
        let allAcceptedRequests: any[] = [];
        
        if (Array.isArray(acceptedData)) {
          allAcceptedRequests = acceptedData;
        } else if (acceptedData.serviceRequests && Array.isArray(acceptedData.serviceRequests)) {
          allAcceptedRequests = acceptedData.serviceRequests;
        } else if (acceptedData.data) {
          if (Array.isArray(acceptedData.data)) {
            allAcceptedRequests = acceptedData.data;
          } else if (acceptedData.data.serviceRequests && Array.isArray(acceptedData.data.serviceRequests)) {
            allAcceptedRequests = acceptedData.data.serviceRequests;
          }
        }
        
        // Filter to only those for this property AND without scheduledDate
        const unscheduledAccepted = allAcceptedRequests.filter((req: any) => {
          const hasScheduledDate = req.scheduledDate && req.scheduledDate !== null;
          if (hasScheduledDate) return false; // Skip if already scheduled
          
          const reqAddress = normalizeAddress(req.address || req.customerId?.address);
          return jobBelongsToProperty(req, reqAddress);
        });
        
        allJobs.push(...unscheduledAccepted);
        console.log('[ADD-TO-TODAY] Found unscheduled accepted requests for property:', {
          total: unscheduledAccepted.length,
          filteredFrom: allAcceptedRequests.length,
          propertyCustomerId
        });
      }

      // IMPORTANT: Also fetch ALL ServiceRequests for this property (any status except cancelled)
      // This ensures we detect ServiceRequests that were created but not yet scheduled
      // This prevents creating virtual services when a real ServiceRequest already exists
      // CRITICAL: Must specify status filter to get accepted/scheduled services (endpoint defaults to pending/bidding without it)
      let allCustomerServiceRequests: any[] = [];
      try {
        const allServiceRequestsResponse = await apiService.get('/service-requests', {
          userType: 'provider',
          status: 'accepted,scheduled,in-progress,completed', // Include all active statuses, not just pending/bidding
          limit: 1000 // Get all to filter by property
        });
        
        if (allServiceRequestsResponse.success && allServiceRequestsResponse.data) {
          const allRequestsData = allServiceRequestsResponse.data as any;
          const allServiceRequests = Array.isArray(allRequestsData) 
            ? allRequestsData 
            : (allRequestsData.serviceRequests && Array.isArray(allRequestsData.serviceRequests))
              ? allRequestsData.serviceRequests
              : [];
          
          // CRITICAL: Filter to only this property's requests (by existingCustomerId or customerId === linkedCustomerId + address)
          const customerServiceRequests = allServiceRequests.filter((req: any) => {
            const reqAddress = normalizeAddress(req.address || req.customerId?.address);
            return jobBelongsToProperty(req, reqAddress) && req.status !== 'cancelled' && req.status !== 'pending_cancellation';
          });
          
          // Store for date calculation utility
          allCustomerServiceRequests = customerServiceRequests;
          
          // Add any ServiceRequests that aren't already in allJobs
          customerServiceRequests.forEach((req: any) => {
            const reqId = (req._id || req.id)?.toString();
            const alreadyInJobs = allJobs.some((job: any) => {
              const jobId = (job._id || job.id)?.toString();
              return jobId === reqId;
            });
            
            if (!alreadyInJobs) {
              allJobs.push(req);
              console.log('[ADD-TO-TODAY] Found existing ServiceRequest not in scheduled/accepted lists:', {
                id: reqId,
                serviceType: req.serviceType?.name || req.serviceType?.category,
                status: req.status,
                address: req.address ? `${req.address.street}, ${req.address.city}` : 'No address',
                scheduledDate: req.scheduledDate
              });
            }
          });
        }
      } catch (err) {
        console.warn('[ADD-TO-TODAY] Failed to fetch all ServiceRequests (non-critical):', err);
        // Don't fail the whole operation if this fails
      }

      // Add services from ExistingCustomer records that don't have ServiceRequests yet
      // This handles per-instance services and services that haven't been scheduled
      if (existingCustomerResponse.success && existingCustomerResponse.data) {
        const allExistingCustomers = Array.isArray(existingCustomerResponse.data) 
          ? existingCustomerResponse.data 
          : [];
        
        console.log('[ADD-TO-TODAY] All existing customer records:', {
          total: allExistingCustomers.length,
          records: allExistingCustomers.map((ec: any) => ({
            id: ec._id || ec.id,
            firstName: ec.firstName,
            lastName: ec.lastName,
            linkedCustomerId: ec.linkedCustomerId?._id || ec.linkedCustomerId?.id || ec.linkedCustomerId,
            address: ec.address ? `${ec.address.street}, ${ec.address.city}` : 'No address',
            services: {
              lawnMowing: ec.services?.lawnMowingTrimming,
              weedControl: ec.services?.weedControlFertilizing,
              snowRemoval: ec.services?.snowRemoval
            },
            servicePricing: {
              lawnMowing: ec.servicePricing?.lawnMowingTrimming?.price,
              weedControl: ec.servicePricing?.weedControlFertilizing?.price,
              snowRemoval: ec.servicePricing?.snowRemoval?.price
            }
          }))
        });
        
        // CRITICAL: Filter to only the selected property (match by property-specific _id and address)
        // This ensures we only show services for the selected property, not all properties
        const existingCustomers = allExistingCustomers.filter((ec: any) => {
          const ecId = (ec._id || ec.id)?.toString();
          const ecAddress = normalizeAddress(ec.address);
          
          // Match by property-specific ID
          const idMatches = ecId === propertyCustomerId;
          
          // Also match by address for extra safety
          const addressMatches = normalizedPropertyAddress && ecAddress
            ? ecAddress.street === normalizedPropertyAddress.street && 
              ecAddress.zipCode === normalizedPropertyAddress.zipCode
            : true; // If address not available, rely on ID match
          
          const matches = idMatches && addressMatches;
          
          if (matches) {
            console.log('[ADD-TO-TODAY] Matched ExistingCustomer record (selected property):', {
              id: ec._id || ec.id,
              address: ec.address ? `${ec.address.street}, ${ec.address.city}` : 'No address',
              propertyCustomerId,
              services: ec.services,
              servicePricing: ec.servicePricing
            });
          }
          return matches;
        });
        
        console.log('[ADD-TO-TODAY] Found existing customer records for selected property:', {
          count: existingCustomers.length,
          propertyCustomerId,
          selectedCustomerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
          address: propertyAddress ? `${propertyAddress.street}, ${propertyAddress.zipCode}` : 'N/A',
          records: existingCustomers.map((ec: any) => ({
            id: ec._id || ec.id,
            address: ec.address ? `${ec.address.street}, ${ec.address.city}` : 'No address',
            services: ec.services,
            servicePricing: ec.servicePricing,
            snowRemovalEnabled: ec.services?.snowRemoval,
            snowRemovalPrice: ec.servicePricing?.snowRemoval?.price,
            snowRemovalFrequency: ec.servicePricing?.snowRemoval?.frequency
          }))
        });
        
        // For each ExistingCustomer record (customer may have multiple properties), create virtual ServiceRequest objects for services
        existingCustomers.forEach((ec: any) => {
          // Check each service type
          const serviceTypes = [
            { key: 'lawnMowingTrimming', category: 'mowing', name: 'Lawn Mowing/Trimming' },
            { key: 'weedControlFertilizing', category: 'fertilizing', name: 'Weed Control/Fertilizing' },
            { key: 'snowRemoval', category: 'snow_removal', name: 'Snow Removal' }
          ];
          
          serviceTypes.forEach(({ key, category, name }) => {
            // Check if this service has pricing (match CustomerScheduleDialog logic - don't check services flag)
            // The services flag may be outdated, but if servicePricing exists with a valid price, the service is available
            const service = ec.servicePricing?.[key];
            const servicePrice = service?.price;
            
            // For seasonal contracts, check seasonalContractDetails.totalContractPrice as fallback
            const seasonalContractPrice = service?.seasonalContractDetails?.totalContractPrice;
            const hasValidPrice = servicePrice !== undefined && servicePrice !== null && typeof servicePrice === 'number' && servicePrice > 0;
            const hasSeasonalPrice = !hasValidPrice && seasonalContractPrice !== undefined && seasonalContractPrice !== null && typeof seasonalContractPrice === 'number' && seasonalContractPrice > 0;
            const effectivePrice = hasValidPrice ? servicePrice : (hasSeasonalPrice ? seasonalContractPrice : null);
            
            console.log('[ADD-TO-TODAY] Checking service:', {
              property: `${ec.address?.street}, ${ec.address?.city}`,
              service: name,
              key: key,
              hasService: !!service,
              hasPrice: !!servicePrice,
              price: servicePrice,
              priceType: typeof servicePrice,
              priceIsZero: servicePrice === 0,
              hasSeasonalContractDetails: !!service?.seasonalContractDetails,
              seasonalContractDetails: service?.seasonalContractDetails,
              hasSeasonalContractPrice: !!seasonalContractPrice,
              seasonalContractPrice: seasonalContractPrice,
              seasonalContractPriceType: typeof seasonalContractPrice,
              seasonalContractPriceIsZero: seasonalContractPrice === 0,
              hasValidPrice,
              hasSeasonalPrice,
              effectivePrice,
              frequency: service?.frequency,
              fullServiceObject: service // Log entire service object to see structure
            });
            
            // Match CustomerScheduleDialog: Only require service exists in servicePricing with valid price
            // Don't check ec.services?.[key] flag - it may be outdated/incorrect
            // CRITICAL: Require valid price - skip services with bad/old data to prevent cross-contamination
            if (!service || !effectivePrice) {
              console.log('[ADD-TO-TODAY] Skipping service (no service object or no valid price):', {
                service: name,
                address: `${ec.address?.street}, ${ec.address?.city}`,
                hasService: !!service,
                effectivePrice,
                note: service && !effectivePrice ? 'Service exists but has no valid price - appears to be bad/old data. Skipping to prevent cross-contamination.' : 'No service object found'
              });
              return; // Skip if no service or no valid price (don't use fallbacks to prevent cross-contamination)
            }
            
            // CRITICAL: Check if we already have a ServiceRequest for this EXACT service + address combination
            // This ensures each property's services are correctly separated
            const existingJob = allJobs.find((job: any) => {
              const jobAddress = job.address;
              const ecAddress = ec.address;
              
              // Match by: same street, same zipCode, AND same service category
              // This ensures we don't create duplicates for the same property + service
              const sameAddress = jobAddress && ecAddress &&
                jobAddress.street?.toLowerCase().trim() === ecAddress.street?.toLowerCase().trim() &&
                jobAddress.zipCode === ecAddress.zipCode &&
                job.serviceType?.category === category;
              
              if (sameAddress) {
                console.log('[ADD-TO-TODAY] Found existing ServiceRequest for this property + service:', {
                  property: `${ecAddress.street}, ${ecAddress.city}`,
                  service: name,
                  existingJobId: job._id || job.id,
                  existingJobAddress: `${jobAddress.street}, ${jobAddress.city}`,
                  existingJobService: job.serviceType?.name || job.serviceType?.category
                });
              }
              
              return sameAddress;
            });
            
            if (existingJob) {
              console.log('[ADD-TO-TODAY] Skipping virtual ServiceRequest - already exists:', {
                service: name,
                property: `${ec.address.street}, ${ec.address.city}`,
                existingJobId: existingJob._id || existingJob.id,
                existingJobType: existingJob.isVirtual ? 'virtual' : 'real'
              });
            } else {
              // If no ServiceRequest exists for this service + address, create a virtual one
              // CRITICAL: Use the address from the ExistingCustomer record (ec.address) to ensure correct property
              const virtualServiceRequest = {
                _id: `virtual-${ec._id}-${key}`, // Virtual ID for tracking
                id: `virtual-${ec._id}-${key}`,
                customerId: propertyCustomerId, // Use property-specific customerId
                providerId: ec.providerId,
                serviceType: {
                  category: category,
                  name: name
                },
                address: {
                  // Use the EXACT address from the ExistingCustomer record for this property
                  street: ec.address.street,
                  city: ec.address.city,
                  state: ec.address.state,
                  zipCode: ec.address.zipCode,
                  coordinates: ec.address.coordinates
                },
                frequency: service?.frequency || 'per-instance',
                status: 'accepted', // Treat as accepted since customer is onboarded
                scheduledDate: null, // Not scheduled yet
                estimatedDuration: 0.5, // Default
                price: effectivePrice, // Use effective price (regular or seasonal contract)
                isVirtual: true, // Flag to indicate this is a virtual ServiceRequest
                existingCustomerId: ec._id,
                serviceKey: key
              };
              
              allJobs.push(virtualServiceRequest);
              console.log('[ADD-TO-TODAY] Created virtual ServiceRequest for:', {
                service: name,
                property: `${ec.address.street}, ${ec.address.city}`,
                frequency: virtualServiceRequest.frequency,
                price: virtualServiceRequest.price,
                existingCustomerId: ec._id,
                serviceKey: key,
                address: virtualServiceRequest.address
              });
            }
          });
        });
        
        console.log('[ADD-TO-TODAY] Total jobs after adding virtual ServiceRequests:', {
          total: allJobs.length,
          jobs: allJobs.map((j: any) => ({
            id: j._id || j.id,
            isVirtual: j.isVirtual,
            serviceType: j.serviceType?.name || j.serviceType?.category,
            customerId: j.customerId,
            address: j.address ? `${j.address.street}, ${j.address.zipCode}` : 'No address',
            status: j.status,
            scheduledDate: j.scheduledDate
          }))
        });
      }

      // Filter to only this customer's jobs FOR THIS SPECIFIC PROPERTY ADDRESS
      // Multi-property customers should only see services for the selected property
      const selectedAddress = selectedCustomer.address;
      
      console.log('[ADD-TO-TODAY] Filtering jobs by property:', {
        totalJobs: allJobs.length,
        selectedCustomerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        selectedAddress: selectedAddress ? `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zipCode}` : 'No address',
        propertyCustomerId: propertyCustomerId,
        normalizedPropertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A'
      });
      
      const filteredJobs = allJobs.filter((job: any) => {
        const jobAddress = normalizeAddress(job.address || job.customerId?.address);
        const matches = jobBelongsToProperty(job, jobAddress);
        
        if (!matches) {
          const jobExistingCustomerId = (job.existingCustomerId?._id || job.existingCustomerId?.id || job.existingCustomerId)?.toString();
          const jobCustomerId = (job.customerId?._id || job.customerId?.id || job.customerId)?.toString();
          console.log('[ADD-TO-TODAY] Job filtered out - not this property:', {
            jobCustomerId,
            jobExistingCustomerId,
            propertyCustomerId,
            linkedCustomerIdStr,
            jobService: job.serviceType?.name || job.serviceType?.category
          });
          return false;
        }
        
        return true;
      });

      // Remove duplicates
      const uniqueJobs = filteredJobs.filter((job: any, index: number, self: any[]) => {
        const jobId = (job._id || job.id)?.toString();
        return index === self.findIndex((j: any) => (j._id || j.id)?.toString() === jobId);
      });


      console.log('[ADD-TO-TODAY] Filtered jobs for customer:', {
        customerJobs: uniqueJobs.length,
        filteredFrom: filteredJobs.length,
        totalBeforeFilter: allJobs.length,
        jobs: uniqueJobs.map((j: any) => ({
          id: j._id || j.id,
          isVirtual: j.isVirtual,
          serviceType: j.serviceType?.name || j.serviceType?.category,
          scheduledDate: j.scheduledDate,
          status: j.status,
          address: j.address ? `${j.address.street}, ${j.address.zipCode}` : 'No address',
          frequency: j.frequency || 'N/A',
          customerId: j.customerId,
          existingCustomerId: j.existingCustomerId
        }))
      });
      
      // Log jobs that were filtered out for debugging
      const filteredOutJobs = allJobs.filter((job: any) => {
        const jobAddress = normalizeAddress(job.address || job.customerId?.address);
        return !jobBelongsToProperty(job, jobAddress);
      });
      
      if (filteredOutJobs.length > 0) {
        console.log('[ADD-TO-TODAY] Jobs filtered out (not matching property):', {
          count: filteredOutJobs.length,
          jobs: filteredOutJobs.map((j: any) => {
            const jAddress = normalizeAddress(j.address);
            const propAddress = normalizedPropertyAddress;
            return {
              id: j._id || j.id,
              isVirtual: j.isVirtual,
              serviceType: j.serviceType?.name || j.serviceType?.category,
              jobCustomerId: (j.customerId?._id || j.customerId?.id || j.customerId)?.toString(),
              propertyCustomerId,
              customerIdMatches: (j.customerId?._id || j.customerId?.id || j.customerId)?.toString() === propertyCustomerId,
              jobAddress: jAddress ? `${jAddress.street}, ${jAddress.zipCode}` : 'No address',
              propertyAddress: propAddress ? `${propAddress.street}, ${propAddress.zipCode}` : 'N/A',
              addressMatches: propAddress && jAddress
                ? jAddress.street === propAddress.street && 
                  jAddress.zipCode === propAddress.zipCode
                : 'N/A'
            };
          })
        });
      }

      setCustomerJobs(uniqueJobs);
    } catch (err: any) {
      console.error('[ADD-TO-TODAY] Failed to fetch customer jobs:', err);
      setError(err.message || 'Failed to load customer jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const formatServiceTypeName = (job: ScheduleJob): string => {
    if (job.serviceType?.name) {
      return job.serviceType.name;
    }
    if (job.serviceType?.category) {
      const category = job.serviceType.category;
      if (category === 'mowing') return 'Lawn Mowing';
      if (category === 'fertilizing') return 'Weed Control/Fertilizing';
      if (category === 'snow_removal') return 'Snow Removal';
      return category;
    }
    return 'Service';
  };

  const formatFrequency = (freq: string): string => {
    const freqMap: { [key: string]: string } = {
      'weekly': 'Weekly',
      'bi-weekly': 'Bi-weekly',
      'monthly': 'Monthly',
      'per-instance': 'Per Instance',
      'seasonal-contract': 'Seasonal Contract',
    };
    return freqMap[freq] || freq;
  };

  const isScheduledForToday = (job: ScheduleJob): boolean => {
    if (!job.scheduledDate) return false;
    // Use standardized date comparison utility
    return isToday(job.scheduledDate);
  };

  const handleAddToToday = async (resetRecurringSchedule: boolean | null = null) => {
    if (!selectedCustomer || !selectedJob) {
      setError('Please select a customer and service');
      return;
    }

    // Use default duration of 0.5 hours if not provided
    const defaultDuration = 0.5;

    // Get actual frequency - for seasonal contracts, extract serviceFrequency from seasonalContractDetails
    let actualFrequency = selectedJob.frequency;
    if (actualFrequency === 'seasonal-contract' && selectedJob.acceptedBid?.servicePricing) {
      const serviceCategory = selectedJob.serviceType?.category;
      const matchingServicePricing = selectedJob.acceptedBid.servicePricing.find((sp: any) => {
        if (serviceCategory === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
        if (serviceCategory === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
        if (serviceCategory === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
        return false;
      });
      
      if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
        actualFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
        console.log('[ADD-TO-TODAY] Extracted serviceFrequency from seasonal contract:', actualFrequency);
      }
    }
    
    // Check if this is a recurring service - if so, ask if it's one-time or permanent
    const isRecurring = actualFrequency && 
      actualFrequency !== 'per-instance' && 
      actualFrequency !== 'seasonal-contract';
    
    // Check if the day of week (weekly/bi-weekly) or day of month (monthly) is changing
    // Get the original scheduled date
    const originalScheduledDate = selectedJob.scheduledDate || selectedJob.nextServiceDate;
    let dayIsChanging = false;
    
    if (isRecurring && originalScheduledDate) {
      // Helper to get day of week from date string
      const getDayOfWeek = (dateString: string | Date): number => {
        if (!dateString) return -1;
        const date = typeof dateString === 'string' 
          ? new Date(dateString + 'T00:00:00') // Parse as local date
          : dateString;
        return date.getDay(); // 0 = Sunday, 1 = Monday, etc.
      };
      
      // Helper to get day of month from date string
      const getDayOfMonth = (dateString: string | Date): number => {
        if (!dateString) return -1;
        const date = typeof dateString === 'string' 
          ? new Date(dateString + 'T00:00:00') // Parse as local date
          : dateString;
        return date.getDate(); // Returns 1-31
      };
      
      // For monthly services, check day of month; for weekly/bi-weekly, check day of week
      if (actualFrequency === 'monthly') {
        const originalDayOfMonth = getDayOfMonth(originalScheduledDate);
        const todayDayOfMonth = new Date().getDate();
        dayIsChanging = originalDayOfMonth !== todayDayOfMonth;
        
        console.log('[ADD-TO-TODAY] Day of month change check:', {
          originalScheduledDate,
          originalDayOfMonth,
          todayDayOfMonth,
          dayIsChanging,
          isRecurring,
          frequency: actualFrequency
        });
      } else {
      const originalDay = getDayOfWeek(originalScheduledDate);
      const todayDay = new Date().getDay();
      dayIsChanging = originalDay !== todayDay;
      
        console.log('[ADD-TO-TODAY] Day of week change check:', {
        originalScheduledDate,
        originalDay,
        todayDay,
        dayIsChanging,
          isRecurring,
          frequency: actualFrequency
      });
      }
    }
    
    // Only show dialog if:
    // 1. Service is recurring
    // 2. Day of week is changing (or no scheduled date exists, meaning we're setting it for the first time)
    // 3. Dialog is not already open
    // 4. resetRecurringSchedule hasn't been set yet (null means user hasn't made a choice)
    if (isRecurring && (dayIsChanging || !originalScheduledDate) && !changeTypeDialog.open && resetRecurringSchedule === null) {
      // Show dialog to ask if this is one-time or permanent
      const handleConfirm = (isPermanent: boolean) => {
        console.log('[ADD-TO-TODAY] Dialog confirmed:', { isPermanent });
        setChangeTypeDialog({ open: false, onConfirm: () => {} });
        // Call handleAddToToday with the flag, bypassing the dialog check
        handleAddToToday(isPermanent);
      };
      
      setChangeTypeDialog({
        open: true,
        onConfirm: handleConfirm
      });
      return;
    }

    // If resetRecurringSchedule is still null and it's not recurring, default to false
    const finalResetRecurringSchedule = resetRecurringSchedule !== null ? resetRecurringSchedule : false;

    try {
      setLoading(true);
      setError(null);

      const jobId = selectedJob._id || (selectedJob as any).id;
      const isVirtual = (selectedJob as any).isVirtual === true;
      const linkedCustomerIdObj = selectedCustomer.linkedCustomerId as any;
      const expectedCustomerId = (linkedCustomerIdObj?._id || linkedCustomerIdObj?.id || linkedCustomerIdObj)?.toString();

      console.log('[ADD-TO-TODAY] Adding service to today:', {
        jobId,
        isVirtual,
        serviceType: formatServiceTypeName(selectedJob),
        scheduledDate: todayDate,
        estimatedDuration: defaultDuration,
        expectedCustomerId,
        resetRecurringScheduleParam: resetRecurringSchedule,
        finalResetRecurringSchedule,
        isRecurring,
        changeType: finalResetRecurringSchedule ? 'PERMANENT' : 'ONE-TIME'
      });

      let response;

      // If this is a virtual ServiceRequest (from ExistingCustomer), we need to create it first
      if (isVirtual) {
        const existingCustomerId = (selectedJob as any).existingCustomerId;
        const serviceKey = (selectedJob as any).serviceKey;
        
        // CRITICAL: Ensure we're using the correct address from the virtual service
        // The address should match the ExistingCustomer record's address for this property
        const serviceAddress = selectedJob.address;
        
        console.log('[ADD-TO-TODAY] Creating ServiceRequest from ExistingCustomer:', {
          existingCustomerId,
          serviceKey,
          serviceType: {
            category: selectedJob.serviceType.category,
            name: selectedJob.serviceType.name
          },
          address: serviceAddress,
          frequency: selectedJob.frequency,
          selectedJobId: selectedJob._id || selectedJob.id,
          virtualServiceDetails: {
            isVirtual: true,
            existingCustomerId,
            serviceKey
          }
        });

        // Validate that we have the correct address
        if (!serviceAddress || !serviceAddress.street || !serviceAddress.zipCode) {
          throw new Error('Invalid address: Missing street or zipCode for service request');
        }

        // Create ServiceRequest from ExistingCustomer record using provider endpoint
        // This ensures the correct address and service type are used for this specific property
        let serviceRequestId: string;
        
        try {
          const createResponse = await apiService.post('/service-requests/from-existing-customer', {
            existingCustomerId: existingCustomerId,
            serviceKey: serviceKey,
            scheduledDate: null, // Will be scheduled separately
            estimatedDuration: defaultDuration
          }, { timeout: 30000 });

          // ServiceRequest was created successfully
          const createResponseData = createResponse.data as any;
          serviceRequestId = createResponseData._id || createResponseData.id;
          console.log('[ADD-TO-TODAY] Created ServiceRequest, now scheduling:', serviceRequestId);
        } catch (createError: any) {
          // Check if the error is because a ServiceRequest already exists
          // The backend returns the existing ServiceRequest in the error's raw data
          if (createError.status === 400 && 
              (createError.message?.includes('already exists') || createError.raw?.error === 'ServiceRequest already exists') &&
              createError.raw?.data) {
            console.log('[ADD-TO-TODAY] ServiceRequest already exists, using existing one:', createError.raw.data);
            const existingServiceRequest = createError.raw.data as any;
            serviceRequestId = existingServiceRequest._id || existingServiceRequest.id;
            // Don't throw error - we're handling this gracefully by using the existing ServiceRequest
          } else {
            // Re-throw other errors
            throw createError;
          }
        }
        
        // Schedule the ServiceRequest (whether newly created or existing)
        // Don't set scheduledTime when adding to today - we don't use time slots
        response = await apiService.post(`/service-requests/${serviceRequestId}/schedule`, {
          scheduledDate: todayDate,
          scheduledTime: null, // No time slot - just date-based scheduling
          estimatedDuration: defaultDuration,
          resetRecurringSchedule: finalResetRecurringSchedule, // Use user's choice
          expectedCustomerId: expectedCustomerId
        }, { timeout: 30000 });
      } else {
        // Regular ServiceRequest - just schedule it
        // Don't set scheduledTime when adding to today - we don't use time slots
        response = await apiService.post(`/service-requests/${jobId}/schedule`, {
          scheduledDate: todayDate,
          scheduledTime: null, // No time slot - just date-based scheduling
          estimatedDuration: defaultDuration,
          resetRecurringSchedule: finalResetRecurringSchedule, // Use user's choice
          expectedCustomerId: expectedCustomerId
        }, { timeout: 30000 });
      }

      if (response.success) {
        console.log('[ADD-TO-TODAY] Service added to today successfully');
        // Close dialog immediately (optimistic UI)
        onClose();
        // Trigger refresh in background (non-blocking)
        onSuccess();
      } else {
        setError(response.error || response.message || 'Failed to add service to today');
      }
    } catch (err: any) {
      console.error('[ADD-TO-TODAY] Error adding service to today:', err);
      
      // Check if this is a timeout or network error - the update may have succeeded
      const isTimeout = err.isTimeout || err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('ERR_NETWORK');
      const isNetworkError = err.isNetworkError || (!err.response && err.request) || err.message?.includes('Network error');
      
      if (isTimeout || isNetworkError) {
        // For timeout/network errors, the update likely succeeded on the backend
        // Close dialog and trigger refresh to verify
        console.log('[ADD-TO-TODAY] Timeout/network error - closing dialog and refreshing to verify');
        onClose();
        // Trigger refresh in background
        onSuccess();
      } else {
        // For actual errors, show error message
        setError(err.message || 'Failed to add service to today');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add to Today's Schedule</DialogTitle>
      <DialogContent>
        {loadingCustomers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter By</InputLabel>
                <Select
                  value={filterBy}
                  label="Filter By"
                  onChange={(e) => setFilterBy(e.target.value as any)}
                >
                  <MenuItem value="all">All Customers</MenuItem>
                  <MenuItem value="customer">Customer Name</MenuItem>
                  <MenuItem value="lawnMowing">Lawn Mowing</MenuItem>
                  <MenuItem value="weedControl">Weed Control</MenuItem>
                  <MenuItem value="snowRemoval">Snow Removal</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="per-instance">Per Instance</MenuItem>
                  <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Autocomplete
              options={(() => {
                // Filter customers based on selected filter
                let filtered = [...customers];
                
                if (filterBy === 'all') {
                  return filtered;
                }
                
                if (filterBy === 'customer') {
                  // "Customer Name" filter shows all customers (they can type to search)
                  return filtered;
                }
                
                // Filter by service type
                if (filterBy === 'lawnMowing') {
                  filtered = filtered.filter(c => c.services?.lawnMowingTrimming === true);
                } else if (filterBy === 'weedControl') {
                  filtered = filtered.filter(c => c.services?.weedControlFertilizing === true);
                } else if (filterBy === 'snowRemoval') {
                  filtered = filtered.filter(c => c.services?.snowRemoval === true);
                }
                
                // Filter by frequency
                if (filterBy === 'weekly' || filterBy === 'bi-weekly' || filterBy === 'monthly' || filterBy === 'per-instance' || filterBy === 'seasonal-contract') {
                  filtered = filtered.filter(c => {
                    const pricing = c.servicePricing || {};
                    
                    // For "seasonal-contract" filter, show all seasonal contracts
                    if (filterBy === 'seasonal-contract') {
                      return (
                        pricing.lawnMowingTrimming?.frequency === 'seasonal-contract' ||
                        pricing.weedControlFertilizing?.frequency === 'seasonal-contract' ||
                        pricing.snowRemoval?.frequency === 'seasonal-contract'
                      );
                    }
                    
                    // For specific frequency filters (weekly, bi-weekly, monthly, per-instance):
                    // Show customers with that frequency OR seasonal contracts (since seasonal contracts
                    // can have any service frequency, and we want them to show up in frequency filters)
                    const hasDirectFrequency = (
                      pricing.lawnMowingTrimming?.frequency === filterBy ||
                      pricing.weedControlFertilizing?.frequency === filterBy ||
                      pricing.snowRemoval?.frequency === filterBy
                    );
                    
                    const hasSeasonalContract = (
                      pricing.lawnMowingTrimming?.frequency === 'seasonal-contract' ||
                      pricing.weedControlFertilizing?.frequency === 'seasonal-contract' ||
                      pricing.snowRemoval?.frequency === 'seasonal-contract'
                    );
                    
                    // Show if they have the direct frequency OR if they have a seasonal contract
                    // (seasonal contracts should appear in all frequency filters since they can have any service frequency)
                    return hasDirectFrequency || hasSeasonalContract;
                  });
                }
                
                return filtered;
              })()}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={selectedCustomer}
              onChange={(_, newValue) => setSelectedCustomer(newValue)}
              onOpen={() => setIsCustomerInputFocused(true)}
              onClose={() => setIsCustomerInputFocused(false)}
              onInputChange={(_, value) => {
                if (value) {
                  setIsCustomerInputFocused(true);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer"
                  placeholder={isCustomerInputFocused ? "Type Customer Name to Search" : "Select Customer"}
                  margin="normal"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.firstName} {option.lastName}
                    </Typography>
                    {option.address && (
                      <Typography variant="caption" color="text.secondary">
                        {option.address.street}, {option.address.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />

            {selectedCustomer && (
              <>
                <Divider sx={{ my: 2 }} />
                
                {loadingJobs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                ) : customerJobs.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This customer has no scheduled or accepted services. Services need to be accepted first before they can be scheduled.
                  </Alert>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Select Service to Add to Today ({todayDate})
                    </Typography>
                    <List>
                      {(() => {
                        // Display jobs in original order (filtering is done at customer level)
                        const sortedJobs = [...customerJobs];
                        
                        return sortedJobs.map((job) => {
                        const jobId = job._id || (job as any).id;
                        const serviceName = formatServiceTypeName(job);
                        const alreadyScheduledToday = isScheduledForToday(job);
                        
                        return (
                          <ListItem
                            key={jobId}
                            button
                            onClick={() => setSelectedJob(job)}
                            selected={selectedJob?._id === job._id || (selectedJob as any)?.id === jobId}
                            sx={{
                              border: '1px solid',
                              borderColor: selectedJob?._id === job._id || (selectedJob as any)?.id === jobId ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              mb: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" fontWeight="medium">
                                    {serviceName}
                                  </Typography>
                                  {job.status && (
                                    <Chip
                                      label={job.status}
                                      size="small"
                                      color={
                                        job.status === 'scheduled' ? 'success' :
                                        job.status === 'completed' ? 'default' :
                                        job.status === 'in-progress' ? 'warning' :
                                        job.status === 'accepted' ? 'info' :
                                        'default'
                                      }
                                    />
                                  )}
                                  {alreadyScheduledToday && (
                                    <Chip
                                      label="Already scheduled for today"
                                      size="small"
                                      color="warning"
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {/* Show address to distinguish between services for different properties */}
                                  {job.address && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                      {job.address.street}, {job.address.city}, {job.address.state} {job.address.zipCode}
                                    </Typography>
                                  )}
                                  {job.frequency && (
                                    <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                                      Frequency: {formatFrequency(job.frequency)}
                                    </Typography>
                                  )}
                                  {job.estimatedDuration && (
                                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Duration: {job.estimatedDuration} hours
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Service Request ID: {jobId?.toString().slice(-8)}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              {selectedJob?._id === job._id || (selectedJob as any)?.id === jobId ? (
                                <Chip label="Selected" color="primary" size="small" />
                              ) : null}
                            </ListItemSecondaryAction>
                            </ListItem>
                          );
                        });
                      })()}
                    </List>

                  </Box>
                )}
              </>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => handleAddToToday()}
          variant="contained"
          disabled={loading || !selectedCustomer || !selectedJob}
          startIcon={loading ? <CircularProgress size={20} /> : <Add />}
        >
          {loading ? 'Adding...' : "Add to Today's Schedule"}
        </Button>
      </DialogActions>

      {/* One-Time vs Permanent Change Dialog - Matches Edit Customer Schedule format */}
      <Dialog
        open={changeTypeDialog.open}
        onClose={() => setChangeTypeDialog({ open: false, onConfirm: () => {} })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Schedule Change Type
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              This is a recurring service. How would you like to schedule it for today?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
              Is this a permanent change to the recurring schedule, or just a one-time change (e.g., due to weather)?
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  if (changeTypeDialog.onConfirm) {
                    changeTypeDialog.onConfirm(false);
                  }
                }}
                sx={{ mb: 2 }}
              >
                One-Time Change
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, textAlign: 'center' }}>
                Only change this specific service date. The recurring schedule will remain unchanged.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  if (changeTypeDialog.onConfirm) {
                    changeTypeDialog.onConfirm(true);
                  }
                }}
              >
                Permanent Change
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
                Update the recurring schedule to the new day for all future services.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AddToTodayDialog;

